import Vue from 'vue';
import VueRouter from 'vue-router';
import api from '@/mixins/api';
import tabsData from '@/mixins/tabsData';
import store from '@/store/index';
import { trackEvent } from '@/utils/analytics/mixpanel';

Vue.use(VueRouter);

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

const routes = [
  {
    path: '/',
    name: 'home',
    redirect: () => ({ path: '/dataCenter/overview' }),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import(/* webpackChunkName: "logout" */ '../views/Logout.vue'),
  },
  {
    path: '/refresh',
    name: 'refresh',
    component: () => import(/* webpackChunkName: "refresh" */ '../views/Refresh.vue'),
  },
  {
    path: '/dataCenter',
    name: 'dataCenter',
    redirect: () => ({ path: '/dataCenter/overview' }),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/dataCenter/:tab',
    name: 'AuthorDataCenterView',
    component: () => import(/* webpackChunkName: "dataCenterForum" */ '../views/AuthorDataCenterView.vue'),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/forum',
    name: 'forum',
    redirect: () => ({ path: '/forum/post' }),
    meta: {
      requireAuth: true,
      requireForum: true,
    },
  },
  {
    path: '/forum/post',
    name: 'forumPost',
    component: () => import(/* webpackChunkName: "forumPost" */ '../views/ForumPost.vue'),
    meta: {
      requireAuth: true,
      requireForum: true,
    },
  },
  {
    path: '/manage',
    name: 'manage',
    redirect: () => ({ path: '/manage/video' }),
    meta: {
      requireAuth: true,
      requireApp: true,
    },
  },
  {
    path: '/manage/video',
    name: 'manageVideo',
    component: () => import(/* webpackChunkName: "manageVideo" */ '../views/ManageVideo.vue'),
    meta: {
      requireAuth: true,
      requireApp: true,
    },
  },
  {
    path: '/manage/article',
    name: 'manageArticle',
    component: () => import(/* webpackChunkName: "manageArticle" */ '../views/ManageArticle.vue'),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/manage/live',
    name: 'manageLive',
    component: () => import(/* webpackChunkName: "manageLive" */ '../views/ManageLive.vue'),
    meta: {
      requireAuth: true,
      requireApp: true,
    },
  },
  {
    path: '/tool',
    name: 'tool',
    redirect: () => ({ path: '/tool/push' }),
    meta: {
      requireAuth: true,
      requireApp: true,
    },
  },
  {
    path: '/tool/push',
    name: 'toolPushView',
    component: () => import(/* webpackChunkName: "toolPush" */ '@/views/tool/Push.vue'),
    meta: {
      requireAuth: true,
      requireApp: true,
    },
  },
  {
    path: '/tool/shortUrl',
    name: 'toolShortUrl',
    component: () => import(/* webpackChunkName: "toolShortUrl" */ '@/views/tool/ShortUrl.vue'),
    meta: {
      requireAuth: true,
      requireAppWithLink: true,
    },
  },
  // {
  //   path: '/author',
  //   name: 'author',
  //   redirect: () => ({ path: '/author/performance' }),
  //   meta: {
  //     requireAuth: true,
  //   },
  // },
  // {
  //   path: '/author/performance',
  //   name: 'authorPerformance',
  //   component: () =>
  // import(/* webpackChunkName: "authorPerformance" */ '../views/AuthorPerformance.vue'),
  //   meta: {
  //     requireAuth: true,
  //   },
  // },
  {
    path: '/help',
    name: 'help',
    redirect: () => ({ path: '/help/teach' }),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/help/teach',
    name: 'helpTeach',
    component: () => import(/* webpackChunkName: "helpTeach" */ '../views/HelpTeach.vue'),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/help/contact',
    name: 'helpContact',
    component: () => import(/* webpackChunkName: "helpContact" */ '../views/AuthorDefaultView.vue'),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: '*', redirect: '/',
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.VUE_APP_BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  window.scrollTo(0, 0);
  // 整頁重整時 Vuex 為空，先等 OIDC userStore 的 token 灌回，再判斷登入
  await store.dispatch('auth/hydrateFromOidc');
  const hasToken = !!store.getters['auth/getTokens'].at;
  if (hasToken) {
    await api.methods.getIsAuthor();
  } else {
    // 沒有有效的 OIDC 登入：清除殘留 token、重置身份，視為未登入
    localStorage.removeItem('author-tool-token');
    store.dispatch('auth/updateToken', { at: '', idt: '', rt: '' });
    store.dispatch('updateIdentity', {
      identity: { isLogin: false, isAuthor: false, isAdmin: false },
    });
  }

  if (store.getters.identity.isLogin === true) {
    if (store.getters.identity.isAuthor === true || store.getters.identity.isAdmin === true) {
      // getAppList 需要 memberId（GET /apps/{authorMemberId}），先確保會員資料已取得，
      // 否則首次載入 /dataCenter/app 會因 appList 為空被守衛導回 overview
      if (!store.getters.isGetMemberInfo) await api.methods.getMemberOldInfo();
      // 登入後若是作者或 admin 取得作者 app, forumIds, app短連結
      await tabsData.methods.getAppList();
      await tabsData.methods.getAuthorForumIds();
      await tabsData.methods.getAppListWithAppLink();
      await tabsData.methods.getPcList();
    }
  }

  if (to.meta.requireAuth) {
    // 未登入，或既非作者也非無敵帳號，轉導到聯絡我們
    // 用 falsy 判斷（而非 === false），避免 identity 欄位為 undefined 時誤放行
    const { isLogin, isAuthor, isAdmin } = store.getters.identity;
    if (!isLogin || (!isAuthor && !isAdmin)) {
      next('/help/contact');
      return;
    }
  }
  // 如果數據中心同學會, app頁, pc頁沒數據, 導至overview頁面
  if (to.name === 'AuthorDataCenterView') {
    if (to.path === '/dataCenter/app') {
      if (store.getters.appList.length > 0) {
        next();
        return;
      }
      next('/dataCenter/overview');
    }
    if (to.path === '/dataCenter/forum') {
      if (store.getters.forumIds.memberId) {
        next();
        return;
      }
      next('/dataCenter/overview');
    }
    if (to.path === '/dataCenter/pc') {
      if (store.getters.pcList.length > 0) {
        next();
        return;
      }
      next('/dataCenter/overview');
    }
  }
  // 如果作者有app，才能進入影音管理、直播管理、推播管理
  if (to.meta.requireApp) {
    if (store.getters.appList.length > 0 || !store.getters.appList) {
      next();
      return;
    }
    // 如果進去的是推播管理，要判斷一下是否能進入短網址，不行的話要轉導到折價券
    if (to.name === 'toolPushView') {
      if (store.getters.appListWithAppLink.length > 0
        || store.getters.identity.isAdmin) {
        next();
        return;
      }
      // TODO: 目前未規劃折價券, 暫時導向首頁
      // next('/tool/coupon');
      next('/');
      return;
    }
    // 剩下影音管理、直播管理都轉導到網誌文
    next('/manage/article');
    return;
  }

  // 如果同學會userId或groupId非0 才能進入同學會貼文
  // 或是無敵帳號
  if (to.meta.requireForum) {
    if (store.getters.forumIds.userId !== 0
      || store.getters.forumIds.groupId !== 0
      || store.getters.identity.isAdmin) {
      next();
      return;
    }
    // TODO: 目前未規劃優選作家, 暫時導向首頁
    // next('/forum/author');
    next('/');
    return;
  }

  // 如果作者有app短連結的app清單 才能進入app短連結管理
  // 或是無敵帳號
  if (to.meta.requireAppWithLink) {
    if (store.getters.appListWithAppLink.length > 0
      || store.getters.identity.isAdmin) {
      next();
      return;
    }
    next('/tool');
    return;
  }
  // 特別處理：若為「股市隱者」則不顯示產品銷售 tab (jira單號：UTILITY-1106)
  if (store.getters.memberInfo.m === 8326462) {
    if (to.path === '/dataCenter/product') {
      next('/dataCenter/overview');
    }
  }

  // eslint-disable-next-line consistent-return
  return next();
});

router.afterEach((to) => {
  trackEvent('Page_View', {
    page_name: to.name || to.path,
    page_path: to.path,
  });
});

export default router;
